// Every piece of money and betting language on the landing page lives here.
// If the gambling framing needs to change, change it in this file only.

export const copy = {
  brand: "BeatFlipz",
  nav: {
    label: "Site",
    tagline: "Beat Battles",
    contests: "Contests",
    rankings: "Rankings",
    howItWorks: "How it works",
    websites: "Websites",
    forProducers: "For Producers",
    forFans: "For Fans",
    winners: "Winners",
    calculator: "Calculator",
    logIn: "Log in",
    join: "Join",
    dashboard: "Dashboard",
    admin: "Admin",
    logOut: "Log out",
    returnToAdmin: "Return to admin",
    makePick: "Make a Pick",
    menu: "Toggle menu",
  },
  notFound: {
    code: "404",
    heading: "Page not found",
    body: "That page doesn't exist, or it moved.",
    home: "Back to BeatFlipz",
    title: "Not found · BeatFlipz",
  },
  hero: {
    // Rendered with a line break between the two parts.
    headline: ["Bet on the", "Best Beats."],
    sub: "Producers compete. Fans back their picks. Winners get paid.",
    enterContest: "Enter a Contest",
    makePick: "Make a Pick",
    payoutLabel: "Paid out this week",
    picksHeading: "Tonight's picks",
  },
  pick: {
    back: "Back",
    backProducer: (producer: string) => `Back ${producer}`,
    odds: "Odds",
    ofPool: (percent: string) => `${percent} of pool`,
    backers: (n: number) => (n === 1 ? "1 backer" : `${n} backers`),
    play: (producer: string) => `Play ${producer}'s beat`,
    pause: (producer: string) => `Pause ${producer}'s beat`,
    mute: "Mute beats",
    unmute: "Unmute beats",
  },
  slip: {
    heading: "Bet slip",
    close: "Close bet slip",
    odds: "Odds",
    share: "Pool share",
    backers: "Backers",
    stakeLabel: "Your stake",
    amountLabel: "Credits",
    // The platform fee, stated plainly (invariant 17), with the winning
    // producer's cut named apart from it. feeTotal is for a feed that only
    // carries the combined take.
    fee: (rake: string, producer: string) =>
      `${rake} platform fee and ${producer} to the winning producer come off the pool before it is split.`,
    feeTotal: (percent: string) => `${percent} comes off the pool before it is split.`,
    winLine: (producer: string) => `If @${producer} wins, you split the pool with everyone who backed them.`,
    footnote: "Credits, not cash. A skill-based pick on a judged result. Odds move until picks close.",
    quickAmounts: [5, 10, 25, 50],
    payoutLabel: "Potential payout",
    estimateNote: "Estimate from the current pool. The contest page sets the final numbers.",
    estimateNoteLive: "Estimate from the current pool. The final split is set when picks close.",
    noPool: "Nobody has backed this pool yet. Your payout depends on who follows.",
    // A market pays out only once two producers are backed; until then a
    // settle would refund every stake.
    noRival: (bonus: string | null) =>
      `Nobody has backed another producer yet. Picks pay out once fans back at least two producers; until then every pick is refunded.${
        bonus ? ` The ${bonus} house bonus goes to the fans who call the winner.` : ""
      }`,
    invalid: "Enter a credit amount above zero.",
    place: "Place Pick",
    placing: "Placing…",
  },
  how: {
    eyebrow: "How it works",
    heading: "Three moves. No entry fees.",
    steps: [
      { title: "Drop a Beat", body: "Producers upload a track and enter a contest. Entry is always free." },
      { title: "Fans Back You", body: "Fans put credits behind the producer they think takes it. Every pick grows the pool." },
      {
        title: "Winners Get Paid",
        body: "When voting closes, the winning producer and the fans who called it split the pool, after the platform fee.",
      },
    ],
    more: "How BeatFlipz works, in full",
  },
  ticker: {
    label: "Recent wins and contests closing soon",
    producer: "Producer",
    won: "won",
    camePlace: (place: string) => `came ${place} place`,
    closesIn: "closes in",
  },
  live: {
    eyebrow: "Live contests",
    heading: ["Pick a battle.", "Back your ear."],
    scrollHint: "Scroll for more →",
    browseAll: "Browse all contests →",
    openContest: "Open contest",
    picksClosed: "Picks closed",
    leftToVote: "Left to vote",
    closingSoon: "Closing soon",
    closesInSr: (left: string) => `Closes in ${left}`,
    badge: "LIVE",
    pool: "Pool",
    emptyPool: "No picks yet",
    closesIn: "Closes in",
    closed: "Voting closed",
    seeded: "House seeded",
    cta: "Make a Pick",
  },
  producer: {
    eyebrow: "For producers",
    headingLead: "No entry fees.",
    // Must say producers get paid: the winning producer takes a cash cut of
    // the pool, not exposure.
    heading: "Get paid from the pool.",
    body: "Drop a beat in front of listeners who judge the work. Fans back you with credits, and when you win, you take a cut of the pool.",
    points: [
      "Free entry on every contest.",
      "Win, and your cut of the pool is paid to you in credits you can cash out.",
      "Judged on the beat, not on follower count.",
    ],
    cta: "Enter a Contest",
    lastPaid: "Latest paid producer",
    earned: "Earned",
    splitHeading: "Where the pool goes",
    split: [
      { label: "Winning producer", note: "A cut of every pool they win" },
      { label: "Fans who called it", note: "Split the rest by what they put in" },
      { label: "Platform fee", note: "Shown on every contest" },
    ],
  },
  fan: {
    eyebrow: "For fans",
    heading: "Your ear pays.",
    body: "Picking a winner is a skill. The pool is whatever the crowd puts in. After the platform fee, it is split between the winning producer and the fans who backed them.",
    points: [
      { title: "You already do this", body: "Every group chat has someone who called the beat before the drop hit." },
      { title: "Backing is a signal", body: "Your credits say which producer deserves the room. That is public, and it counts." },
      {
        title: "Call the underdog",
        body: "The fewer credits behind the winner, the bigger each backer's share of the pool.",
      },
    ],
    cta: "Make a Pick",
  },
  // The earnings calculator. Every figure is an estimate from open battles,
  // and the fan side always says a pick can lose.
  calculator: {
    // Producers keep what they earn: the platform pays the rails out of its
    // own fee (EPIC-024).
    noFees: "No fees deducted. Beatflipz pays card and payout fees.",
    eyebrow: "Earnings calculator",
    heading: "What's your number?",
    pageHeading: "Beat battle earnings calculator",
    fullPage: "Open the full calculator",
    body: "Set what you want to make this month. We'll show the math, using the battles open right now.",
    role: "I'm a",
    producer: "Producer",
    fan: "Fan",
    goal: "Monthly goal",
    goalSr: "Monthly goal in dollars",
    goalInvalid: "Enter a whole-dollar amount.",
    genre: "Genre",
    anyGenre: "Any genre",
    focus: "I care most about",
    money: "Money",
    buzz: "Buzz",
    assumePot: "Assume each pot is",
    assumeBack: "Fans back",
    assumeBackSuffix: "each, on average",
    potsToWin: "in pots you need to win",
    cutNote: (cut: string, live: boolean) =>
      live
        ? `At a ${cut} producer cut, the average across open battles. Your cut is of the whole pot, not only your backers.`
        : `At a ${cut} producer cut, the most a battle pays. Your cut is of the whole pot, not only your backers.`,
    wins: "Battles to win",
    fansPerPot: "Fans per pot",
    cut: "Your cut",
    stepsHeading: "How to get there",
    moneySteps: (wins: number, fans: number, back: string, cut: string) => [
      {
        title: `Enter more than ${wins} ${wins === 1 ? "battle" : "battles"}`,
        body: `You need ${wins} ${wins === 1 ? "win" : "wins"}. Entry is free, and not every beat takes it, so enter more battles than you need to win.`,
      },
      {
        title: `Rally ${fans} fans per battle`,
        body: `At ${back} a back, that fills the pot. Share your Back Me link in your stories and the group chat. Every fan who backs anyone in your battle grows the pot your cut comes from.`,
      },
      {
        title: `Win ${wins}`,
        body: `Your ${cut} of each pot you win lands as credits you can cash out.`,
      },
    ],
    buzzSteps: (genre: string) => [
      {
        title: genre ? `Enter ${genre} battles every week` : "Enter a battle every week",
        body: "Entry is free. Every battle puts your name on a pick card in front of fans who judge the beat.",
      },
      {
        title: "Share your Back Me link",
        body: "A fan who backs you has a reason to come back and hear your next beat.",
      },
      { title: "Climb the rankings", body: "Wins move you up the ranking fans browse." },
      { title: "Send people to your page", body: "Your producer page shows your record and the battles you're in." },
    ],
    browseBattles: "Browse battles →",
    rankings: "See the rankings →",
    yourPage: "Your producer page →",
    join: "Join BeatFlipz →",
    suggestedHeading: "Open battles to enter",
    suggestedPot: "Pot",
    enter: "Enter",
    seeded: "House seeded",
    singleHeading: "One pick that gets you there",
    stackHeading: (n: number) => `Or stack ${n} picks`,
    stackNote: "Each pick covers part of the goal. Every one of them has to win.",
    stake: "Stake",
    ifWins: "back if they win",
    odds: "Odds",
    back: "Back",
    noReach: "No open pick pays that much within its stake limit. A pick only wins what fans put on the other side, so bigger pots pay more.",
    mostOnePick: "The most one pick can make right now",
    mostOnePickVia: (stake: string, username: string, contest: string) => `Stake ${stake} on @${username} in ${contest}, if they win.`,
    noPicks: "No picks are open right now. Check back when the next battle opens.",
    save: "Save my goal",
    saving: "Saving…",
    saveNote: "Saved goals tell us which battles to open next.",
    savedMember: "Goal saved to your account.",
    savedVisitor: "Goal saved.",
    joinToKeep: "Join to keep it with your account →",
    firstHeading: "Be the first to back",
    firstNote:
      "Nobody has backed these yet, so there are no odds to price. Picks pay out once fans back at least two producers.",
    producers: (n: number) => `${n} ${n === 1 ? "producer" : "producers"}`,
    bonus: (amount: string) => `${amount} house bonus`,
    backFirst: "Pick",
    fanFine:
      "Estimates from the live pool, after fees. Odds move as fans back, and a pick can lose: only back what you're fine losing.",
  },
  // /earnings-calculator: the calculator plus plain text search engines can
  // read. The title matches the one Go puts in the shell.
  calculatorPage: {
    title: "Beat Battle Earnings Calculator · BeatFlipz",
    sections: [
      {
        heading: "How producers get paid",
        body: [
          "Entry is free. When you win a battle, you take the producer cut of the whole pot: every credit fans put behind anyone in that battle, not only your own backers. Each battle shows its cut, and it is never more than 20%.",
          "So the math runs backwards from your goal. Divide what you want to make by your cut to get the pots you need to win, then divide by a typical pot to get the wins. At a 20% cut, $300 a month means winning $1,500 in pots: three $500 pots, each about 25 fans backing $20.",
        ],
      },
      {
        heading: "How fans get paid",
        body: [
          "Picking a winner is a skill. Fans back the producer they think takes the battle, and when voting closes, the fans who called it split the pool after the platform fee and the producer's cut, in proportion to what they put in. House-seeded battles add a bonus to that split.",
          "Odds come from how the pool is split, so they move until picks close. The fewer credits behind the winner, the bigger each backer's share, and a pick only wins what fans put on the other producers. If nobody backs a second producer, every pick is refunded.",
        ],
      },
    ],
    faqHeading: "Questions",
    faq: [
      {
        q: "How much can a producer make on BeatFlipz?",
        a: "Your cut of every pot you win. The calculator shows how many wins and how many fans it takes to reach a monthly number, using the battles open right now.",
      },
      {
        q: "Is the result a promise?",
        a: "No. It is an estimate from the current pools. Pots grow and odds move as fans back, and a pick can lose.",
      },
      { q: "How do I get paid?", a: "Winnings land as credits in your account, and you can cash them out from your dashboard." },
      { q: "Does it cost anything to enter a battle?", a: "No. Entry is free on every contest." },
      {
        q: "Why save my goal?",
        a: "Saved goals tell us which battles people want, so we know which ones to open next.",
      },
    ],
  },
  winners: {
    eyebrow: "Winners wall",
    heading: ["Last week's", "winners."],
    list: "Winning producers",
    top: "Top payout of the week",
  },
  auth: {
    brandEyebrow: "Skill-based beat battles",
    brandHeading: "Flip. Compete. Get paid.",
    brandBody:
      "Producers battle on judged beats and fans back the winner with credits. The vote decides, not a bookmaker.",
    brandPoints: [
      { icon: "trophy", text: "Enter judged beat battles and win a cut of the pool" },
      { icon: "lineChart", text: "Track your ranking across every contest you enter" },
      { icon: "wallet", text: "Cash out winnings to PayPal" },
    ],
    rights: "© 2026 BeatFlipz",
    email: "Email address",
    emailPlaceholder: "you@studio.com",
    password: "Password",
    showPassword: "Show password",
    hidePassword: "Hide password",
    google: "Continue with Google",
    googleFailed: "Google sign-in didn't finish. Try again, or use your email and password.",
    or: "or",
    network: "We couldn't reach BeatFlipz. Check your connection and try again.",
    login: {
      title: "Log in · BeatFlipz",
      eyebrow: "Welcome back",
      heading: ["Log in to", "BeatFlipz"],
      sub: "Your contests, picks, and payouts are waiting.",
      passwordPlaceholder: "Your password",
      forgot: "Forgot password?",
      submit: "Log in",
      submitting: "Logging in…",
      missing: "Enter your email and password to continue.",
      footer: "New to BeatFlipz?",
      footerLink: "Create an account",
    },
    register: {
      title: "Join · BeatFlipz",
      eyebrow: "Join the flip",
      heading: ["Create your", "account"],
      sub: "One account to enter battles, back picks with credits, and cash out your winnings.",
      username: "Username",
      usernamePlaceholder: "How the board will know you",
      role: "I am mostly a",
      roles: [
        { value: "artist", label: "Artist" },
        { value: "producer", label: "Producer" },
        { value: "both", label: "Both" },
      ],
      passwordPlaceholder: "At least 8 characters",
      confirm: "Confirm password",
      confirmPlaceholder: "Type it once more",
      strength: "Password strength",
      strengthLabels: ["Too short", "Weak", "Fair", "Good", "Strong"],
      agree: "I agree to the contest rules and confirm I am eligible to compete where I live.",
      submit: "Create account",
      submitting: "Creating account…",
      badUsername: "Pick a username of 3 to 30 letters, numbers, dots, dashes, or underscores.",
      badEmail: "Enter a valid email address.",
      shortPassword: "Your password needs at least 8 characters.",
      mismatch: "Those passwords don't match.",
      mustAgree: "You need to agree to the contest rules to continue.",
      captchaFailed: "We couldn't run the human check. Refresh the page and try again.",
      recaptchaNote: "Protected by reCAPTCHA. Google's Privacy Policy and Terms apply.",
      footer: "Already have an account?",
      footerLink: "Log in",
    },
  },
  websites: {
    title: "Websites for producers · BeatFlipz",
    hero: {
      eyebrow: "BeatFlipz · Websites for producers",
      heading: ["Beat stores sell files.", "Websites build careers."],
      sub: "A professional home for your music, contests, feedback, and wins.",
      build: "Build Your Producer Website",
      pricing: "See Pricing",
      unlocks: "What your website unlocks",
      values: [
        { icon: "trophy", title: "Contests in Motion", body: "Compete in real challenges with deadlines and judges." },
        { icon: "feedback", title: "Feedback That Matters", body: "Written and audio reviews you can reuse and share." },
        { icon: "userStar", title: "No Audience Required", body: "Win based on the beat — not your follower count." },
        { icon: "lineChart", title: "Built for Momentum", body: "Fast contests, clear outcomes, repeat opportunities." },
      ],
    },
    stats: {
      eyebrow: "On the platform",
      contests: "Contests run",
      contestsNote: "Judged battles with deadlines",
      submissions: "Beats entered",
      submissionsNote: "Producers putting work up",
      reviews: "Reviews written",
      reviewsNote: "Feedback producers can reuse",
    },
    audience: {
      eyebrow: "Who this is for",
      heading: "This is for producers who…",
      items: [
        { icon: "music", text: "Already have music, but want leverage" },
        { icon: "userHeart", text: "Want people to interact — not just listen" },
        { icon: "team", text: "Are open to collaboration, sponsorship, and visibility" },
        { icon: "lineChart", text: "Believe momentum beats algorithms" },
      ],
      note: "This isn't for producers who only want to upload and disappear.",
    },
    shift: {
      eyebrow: "The shift",
      heading: "What changes when you have a producer website",
      before: "Before",
      after: "After",
      beforeItems: [
        { icon: "links", text: "Links everywhere" },
        { icon: "userUnfollow", text: "Passive listeners" },
        { icon: "refresh", text: "No reason to return" },
        { icon: "question", text: "No proof or momentum" },
      ],
      afterItems: [
        { icon: "homeHeart", text: "One destination" },
        { icon: "userStar", text: "Fans participate" },
        { icon: "trophy", text: "Wins and reactions visible" },
        { icon: "rocket", text: "Attention compounds into opportunity" },
      ],
    },
    tools: {
      eyebrow: "Interaction tools",
      heading: ["Turn listeners into", "participants"],
      sub: "Your website isn't a brochure. It's where people interact with your journey.",
      items: [
        { icon: "video", title: "Reaction Videos", body: "Let fans and artists record video or audio reactions. Social proof that builds trust." },
        { icon: "feedback", title: "Feedback Requests", body: "Get feedback on works-in-progress from fans and producers." },
        { icon: "notification", title: "Activity Feed", body: "Every drop, win, and reaction becomes content. Keep followers engaged between releases." },
        { icon: "trophy", title: "Contest Entries", body: "Enter beat battles and flip challenges, and show every result on your site." },
      ],
    },
    reactions: {
      eyebrow: "On camera",
      heading: "Let people react to your music",
      sub: "Reactions create trust, proof, and emotional buy-in. They show new visitors that your music moves people.",
      reactedTo: (track: string) => `reviewed ${track}`,
      watch: "Watch the reaction",
      listen: "Hear the reaction",
    },
    cosign: {
      eyebrow: "Co-sign",
      heading: "Let fans back your talent",
      sub: "With Co-Sign, supporters can sponsor your contest entry. If you win, you share the upside. That's alignment — not donations.",
      steps: [
        { icon: "handHeart", title: "Sponsor Backs Producer", body: "A fan or supporter backs your contest entry." },
        { icon: "trophy", title: "Producer Enters Contest", body: "You compete with backing and support behind you." },
        { icon: "rocket", title: "Both Benefit from the Win", body: "If you win, you share the upside." },
      ],
    },
    store: {
      eyebrow: "Integrations",
      heading: "Extend your beat store. Don't replace it.",
      sub: "Bring your BeatStars, Airbit, Tracktrain, or Bandcamp store onto your site. Your website surrounds it with context, proof, and engagement.",
      platforms: [
        { icon: "music", name: "BeatStars" },
        { icon: "headphone", name: "Airbit" },
        { icon: "disc", name: "Tracktrain" },
        { icon: "album", name: "Bandcamp" },
      ],
      embed: "Embed your store",
      keepTitle: "Keep selling on BeatStars",
      keepBody: "Build on BeatFlipz. Your beat store handles transactions. Your website builds relationships.",
      siteLabel: "Your producer website",
      modules: [
        { icon: "video", label: "Reaction Videos" },
        { icon: "trophy", label: "Contest Wins" },
        { icon: "shoppingCart", label: "Embedded Beat Store", store: true },
        { icon: "notification", label: "Activity Feed" },
        { icon: "lineChart", label: "Momentum Stats" },
      ],
    },
    included: {
      eyebrow: "Included",
      heading: "Your BeatFlipz producer website includes",
      items: [
        { icon: "homeHeart", title: "Custom Producer Homepage", body: "Your music, your story, your destination" },
        { icon: "video", title: "Reaction & Feedback Tools", body: "Let fans and producers engage with your work" },
        { icon: "trophy", title: "Contest & Co-Sign Modules", body: "Compete, win, and get sponsored by supporters" },
        { icon: "notification", title: "Activity & Momentum Feed", body: "Every win and reaction becomes content" },
        { icon: "shoppingCart", title: "Beat Store Integrations", body: "Connect BeatStars, Airbit, Tracktrain, and more" },
        { icon: "userAdd", title: "Subscriber & Fan Capture", body: "Build your list and own your audience" },
        { icon: "smartphone", title: "Mobile-First Performance", body: "Fast, responsive, and built for engagement" },
        { icon: "lineChart", title: "Momentum Tracking", body: "See what's working and what's building" },
      ],
    },
    earnings: {
      eyebrow: "Payouts",
      heading: "Producers are already getting paid here",
      sub: "Real contest wins from BeatFlipz producers.",
      win: "Contest win",
    },
    momentum: {
      eyebrow: "Activity",
      heading: "Momentum on BeatFlipz",
      sub: "The latest wins and reactions across the platform.",
      note: "Without a website, none of this momentum would be visible or shareable.",
    },
    pricing: {
      eyebrow: "Pricing",
      heading: "Choose your level",
      sub: "Pick the plan that fits where your career is now. Upgrade when you're ready to compete and monetize.",
      perMonth: "per month",
      recommended: "Recommended",
      redirecting: "Opening checkout…",
      failed: "We couldn't start checkout. Try again in a minute.",
      // `plan` is what POST /v1/subscription/checkout calls the tier.
      tiers: [
        {
          name: "Start",
          plan: "Start",
          price: "$5",
          description: "Claim your presence",
          outcome: "Get a shareable producer page.",
          cta: "Start for $5",
          highlight: false,
          features: [
            { icon: "global", text: "Producer page on BeatFlipz subdomain" },
            { icon: "user", text: "Basic profile (bio + links)" },
            { icon: "music", text: "Showcase tracks (limited)" },
            { icon: "video", text: "Reaction videos viewable" },
            { icon: "searchEye", text: "Appears in discovery" },
          ],
          limitations: [
            "No custom domain",
            "No full website builder",
            "No email capture",
            "No cash contest access",
            "No analytics",
          ],
        },
        {
          name: "Lite",
          plan: "Lite",
          price: "$9",
          description: "Own your domain",
          outcome: "Build credibility with your own URL",
          cta: "Get Lite",
          highlight: false,
          features: [
            { icon: "check", text: "Everything in Start", bold: true },
            { icon: "layout", text: "Full website builder" },
            { icon: "link", text: "Custom domain" },
            { icon: "store", text: "Beat store integration" },
            { icon: "mail", text: "Email capture" },
            { icon: "barChart", text: "Basic analytics" },
            { icon: "video", text: "Reaction videos (paid unlocks)" },
            { icon: "trophy", text: "Limited cash contest eligibility (when funded)" },
          ],
          limitations: [],
        },
        {
          name: "Pro",
          plan: "Premium",
          price: "$19",
          description: "Monetize your talent",
          outcome: "Turn attention into income",
          cta: "Go Pro",
          highlight: true,
          features: [
            { icon: "check", text: "Everything in Lite", bold: true },
            { icon: "trophy", text: "Guaranteed contest eligibility (when funded)" },
            { icon: "videoAdd", text: "Priority access to reaction videos" },
            { icon: "video", text: "Discounted reaction unlocks" },
            { icon: "userStar", text: "Access to higher-tier reviewers" },
            { icon: "handCoin", text: "Co-Sign sponsorship access" },
            { icon: "lineChart", text: "Advanced analytics" },
            { icon: "customerService", text: "Priority support" },
          ],
          limitations: [],
        },
      ],
      footnote: "Pro unlocks real monetization: contests, sponsorships, and advanced tools.",
    },
    final: {
      heading: ["Own your attention.", "Build your career."],
      sub: "Your beat store sells files. Your website captures attention, builds relationships, and unlocks opportunities.",
      trust: ["Plans from $5 a month", "Keep your beat store", "Live in minutes"],
    },
  },
  howPage: {
    title: "How it works · BeatFlipz",
    hero: {
      eyebrow: "How BeatFlipz works",
      heading: ["Producers get paid.", "Fairly."],
      sub: "BeatFlipz is a beat battle platform built so producers get paid for the work, not the follower count. Producers enter contests and vote on each other's tracks with the names hidden. Fans back the producer they think will win, and the pool they build pays the winner.",
      jumpLabel: "On this page",
    },
    jump: [
      { id: "contest", label: "A contest" },
      { id: "voting", label: "Blind voting" },
      { id: "judging", label: "Judging" },
      { id: "picks", label: "Picks and the pool" },
      { id: "need", label: "What you need" },
      { id: "paid", label: "Getting paid" },
      { id: "fees", label: "Fees" },
      { id: "websites", label: "Websites" },
      { id: "refunds", label: "Refunds" },
      { id: "for", label: "Who it's for" },
      { id: "faq", label: "FAQ" },
    ],
    mission: {
      eyebrow: "The mission",
      heading: "Get paid for the beat",
      body: "Most producers never see money from a beat battle. The prize is small, the host keeps the fees, or the winner is whoever has the biggest following. BeatFlipz is built the other way around.",
      points: [
        { icon: "eyeOff", title: "Judged on the track", body: "The voting screen shows Track 1, Track 2, not producer names. You win on how it sounds." },
        { icon: "shieldCheck", title: "Nobody backs themselves", body: "If you're in a contest, you can't pick in it. If you've picked in a contest, you can't enter it." },
        { icon: "coins", title: "Fees up front", body: "Every contest shows its platform fee and the winning producer's cut before anyone picks." },
        { icon: "wallet", title: "Winnings you can cash out", body: "Prizes and pool winnings land in your balance and can be withdrawn to PayPal." },
      ],
    },
    contest: {
      eyebrow: "A contest, start to finish",
      heading: "From upload to payout",
      steps: [
        { who: "Producers", title: "Enter and upload", body: "Join a contest and upload your beat before the deadline." },
        { who: "Fans", title: "Back a producer", body: "Fans listen and put credits behind the producer they think wins. Every pick grows the pool." },
        { who: "Producers", title: "Vote blind", body: "When submissions close, the producers who entered vote on each other's tracks with the names hidden." },
        { who: "BeatFlipz", title: "Name the winner", body: "The BeatFlipz team reviews the vote tally and names the winner. Picks close at that moment." },
        { who: "Winners", title: "Get paid", body: "The pool is split and paid right away. Winnings can be cashed out to PayPal." },
      ],
    },
    voting: {
      eyebrow: "Blind voting",
      heading: "You vote on the sound, not the name",
      body: "Voting is done by the producers who entered the contest. Fans don't vote. They back picks, which is a separate thing and has no effect on the result.",
      rules: [
        "Each entry shows as a numbered track. Producer names stay hidden on the voting screen.",
        "You can't vote for your own track.",
        "You get one vote. Vote again and your new choice replaces the old one.",
        "A vote only counts if you listened to at least 10% of the track you voted for.",
        "You can also rate each track out of five stars.",
      ],
    },
    judging: {
      eyebrow: "Judging",
      heading: "How the winner is decided",
      body: "When voting ends, the BeatFlipz team reviews the tally for every track: its votes and its star ratings. The team names the winner, and a person confirms every result.",
      points: [
        { title: "Votes decide", body: "The tally is built from the entrants' votes, so the result comes from producers judging producers." },
        { title: "A person confirms it", body: "Nothing is picked by an algorithm or at random. The team checks the tally before a winner is named." },
        { title: "Final when named", body: "Naming the winner pays the prize, closes picks and splits the pool, all in one step." },
      ],
    },
    picks: {
      eyebrow: "Picks and the pool",
      heading: "Fans fund the prize",
      body: "Fans put credits on the producer they think will win. Everything they put in is the pool. When the winner is named, the pool splits four ways.",
      exampleLabel: "Example: a $1,000 pool",
      examplePool: "$1,000",
      split: [
        { key: "fee", share: 15, amount: "$150", label: "Platform fee", note: "What BeatFlipz keeps, and it pays the card and payout fees" },
        { key: "backing", share: 5, amount: "$50", label: "Backing share", note: "Paid to every producer, by the picks on them, win or lose" },
        { key: "producer", share: 5, amount: "$50", label: "Winner's cut", note: "Paid to the producer who wins" },
        { key: "fans", share: 75, amount: "$750", label: "Fans who called it", note: "Split by how much each put in" },
      ],
      exampleNote:
        "The 15% fee, 5% backing share and 5% winner's cut are the Standard split. Every contest shows its own numbers before you pick.",
      rulesHeading: "The rules",
      rules: [
        "Picks start at $1, and one person can put up to $1,000 on a contest. Each contest shows its limits.",
        "You need a verified email to pick.",
        "Odds come from how the pool is split and move with every pick. BeatFlipz doesn't set them.",
        "Picks stay open until the winner is named.",
        "If your producer wins, you split the fan share with everyone who backed them, by how much each of you put in. The fewer people on the winner, the bigger each share.",
        "Tapping Place Pick twice only places one pick.",
        "Contests marked House seeded have credits from BeatFlipz in the pool to get it started.",
      ],
    },
    need: {
      eyebrow: "What you need",
      heading: "Before you start",
      groups: [
        {
          icon: "music",
          title: "To enter a contest",
          items: [
            "A free BeatFlipz account",
            "A verified email",
            "Your beat as an mp3 or wav, up to 150 MB",
            "The entry fee, if the contest has one. Many are free.",
          ],
        },
        {
          icon: "headphone",
          title: "To back a producer",
          items: [
            "A free account with a verified email",
            "Credits in your balance, bought from your dashboard",
            "Not being entered in that contest",
          ],
        },
        {
          icon: "paypal",
          title: "To cash out",
          items: ["At least $25 in winnings", "A PayPal email"],
        },
      ],
    },
    paid: {
      eyebrow: "Getting paid",
      heading: "From win to PayPal",
      steps: [
        { title: "Win", body: "Your prize, your cut of the pool, or your share as a fan is added to your balance the moment the winner is named." },
        { title: "Request", body: "From your dashboard, request a withdrawal of $25 or more to your PayPal email." },
        { title: "Get paid", body: "The BeatFlipz team sends the PayPal payment, usually within a few business days." },
      ],
      waysHeading: "What you can win",
      ways: [
        {
          title: "Producers",
          items: [
            "The winning producer's cut of the fan pool",
            "The cash prize, on contests that have one",
            "In a Versus (1v1) battle, most of the entry fees both producers paid",
          ],
        },
        { title: "Fans", items: ["A share of the pool when the producer you backed wins"] },
      ],
      note: "Only winnings can be cashed out. Credits you buy, co-sign credits and refunded picks can be spent on BeatFlipz but not withdrawn.",
    },
    fees: {
      eyebrow: "Fees",
      heading: "Beatflipz pays the fees",
      body: "Beatflipz pays card and payout fees. You keep 100% of what you earn.",
      items: [
        "Buying credits: Beatflipz pays the card fee. $25 buys 25 credits.",
        "Cashing out: Beatflipz pays the PayPal fee. The minimum cash-out is $25.",
        "Credit purchases are final and aren't refunded to your card.",
        "When picks on a contest are called off, every pick comes back to you as credits, and no fee is taken.",
      ],
    },
    websites: {
      eyebrow: "Websites for producers",
      heading: "A home for your music",
      body: "Contests get you heard. A BeatFlipz website gives people somewhere to go next: your tracks, bio, socials, beat store and a mailing list, published at yourname.beatflipz.com.",
      points: [
        "Building your site is free. Publishing it needs a plan.",
        "Embed your beat store and keep selling where you already sell.",
        "Collect emails from the listeners who find you through contests.",
        "You don't need a website to enter contests or get paid.",
      ],
      plansLabel: "Plans",
      perMonth: "/mo",
      cta: "See websites for producers",
    },
    refunds: {
      eyebrow: "Refund policy",
      heading: "When money comes back",
      caption: "What happens to your money in each case",
      columns: { when: "When", what: "What happens" },
      rows: [
        {
          when: "Picks on a contest are called off",
          what: "Every pick is refunded in full, and no fee is taken. Picks are called off when there's no winner, when nobody backed the winner, when every pick was on one producer, or when the contest is cancelled or removed. Refunds come back as credits you can spend.",
        },
        { when: "Your pick didn't go through", what: "Nothing is taken. If your balance was charged and the pick failed, the charge is reversed right away." },
        { when: "A withdrawal is rejected", what: "The full amount goes back to your balance, and you can withdraw it again." },
        { when: "Credits you bought", what: "Credit purchases are final and aren't refunded to your card." },
        { when: "Contest entry fees", what: "Entry fees aren't refunded once you've joined a contest." },
        { when: "Website plans", what: "Cancel any time by emailing beatflipz@gmail.com." },
      ],
    },
    audience: {
      eyebrow: "Who it's for",
      heading: "Built for people with an ear",
      items: [
        {
          icon: "music",
          title: "Producers",
          body: "Beatmakers who want to be judged on the work and paid when they win, whether you have 50 followers or 50,000.",
        },
        {
          icon: "headphone",
          title: "Fans",
          body: "Listeners who can call a winner before the drop. If you use pick'em and odds on sports apps, you already know how this works.",
        },
        {
          icon: "userStar",
          title: "Artists",
          body: "Artists looking for a beat can host a contest and let producers compete for the placement.",
        },
      ],
    },
    faq: {
      eyebrow: "FAQ",
      heading: "Questions",
      items: [
        {
          q: "Is this gambling?",
          a: "BeatFlipz is a skill-based competition. Results come from producers voting on the tracks and are confirmed by the BeatFlipz team, not from chance. Fans pick against each other in a shared pool, and the odds come from that pool, not from BeatFlipz. Play only where you're eligible.",
        },
        { q: "Do I need followers to win?", a: "No. Voters see numbered tracks, not names or follower counts." },
        {
          q: "Can I pick in a contest I entered?",
          a: "No. Entrants can't pick in their own contest, on themselves or anyone else. And once you've picked in a contest, you can't enter it.",
        },
        { q: "Who votes?", a: "The producers who entered the contest. You can't vote for your own track. Fans back producers with picks but don't vote." },
        { q: "When do picks close?", a: "When the winner is named. Until then, the odds keep moving." },
        {
          q: "What are credits?",
          a: "Credits are your BeatFlipz balance, shown in dollars. You use them to pay entry fees and make picks. Winnings are added as credits too, and those can be cashed out.",
        },
        { q: "How long does a cash-out take?", a: "Withdrawals are sent to PayPal by the BeatFlipz team, usually within a few business days. The minimum is $25." },
        { q: "What happens if nobody backs the winner?", a: "The picks are called off and everyone gets back what they put in. No fee is taken." },
        {
          q: "What does House seeded mean?",
          a: "BeatFlipz put credits into that contest's pool to get it started. Those contests are labelled so you know the pool isn't all from fans.",
        },
        {
          q: "Is entry free?",
          a: "Many contests are free. Some have an entry fee, shown before you join. On free contests you can hold up to 3 seats you haven't uploaded to yet. Seats past that cost 10 credits each.",
        },
        { q: "Do I need a website to compete?", a: "No. Contests and payouts work without one." },
      ],
    },
    cta: {
      heading: ["Your beat.", "Your payday."],
      enter: "Enter a Contest",
      pick: "Make a Pick",
      legal: "The full rules are in the",
      terms: "terms of use",
      and: "and",
      privacy: "privacy policy",
    },
  },
  contests: {
    title: "Contests · BeatFlipz",
    eyebrow: "Contests",
    heading: ["Find your next", "battle"],
    sub: "Every contest on the board. Filter by prize, genre, and admission, then drop a beat or back the ear you trust.",
    found: (n: number) => `Found ${n} ${n === 1 ? "contest" : "contests"}`,
    clear: "Clear filters",
    filters: "Filters",
    filtersLabel: "Contest filters",
    results: "Contest results",
    search: "Search",
    active: (n: number) => `${n} active`,
    reset: "Reset",
    host: "Artist / Producer",
    prize: "Prize",
    min: "Min",
    max: "Max",
    seatsOpen: "Seats open",
    myContests: "My contests",
    versus: "Versus",
    freeAdmission: "Free admission",
    genre: "Genre",
    challenge: "Challenge",
    duration: "Duration",
    allHosts: "All producers",
    allGenres: "All genres",
    allChallenges: "All challenges",
    anyDuration: "Any duration",
    open: (title: string) => `Open contest ${title}`,
    cover: (title: string) => `${title} cover art`,
    entered: "You are entered",
    sponsor: "You are the host",
    seeded: "Seeded",
    pool: "Pool",
    cashPrize: "Cash prize",
    prizeLabel: "Prize",
    samplePack: "Sample pack prize",
    free: "Free admission",
    entry: (display: string) => `${display} entry`,
    payAsYouGo: "Pay per entry",
    seatsLeft: (n: number) => `${n} ${n === 1 ? "seat" : "seats"} open`,
    full: "Full",
    closesIn: (t: string) => `Closes in ${t}`,
    judging: "Judging",
    complete: "Complete",
    closed: "Closed",
    wonBy: (username: string) => `Won by ${username}`,
    play: (title: string) => `Play ${title} preview`,
    pause: (title: string) => `Pause ${title} preview`,
    noMatch: "No contests match that",
    noMatchSub: "Loosen a filter or two. New battles open every week.",
    resetFilters: "Reset filters",
    empty: "No contests yet",
    emptySub: "The board is empty right now. New battles open every week.",
    error: "Contests didn't load",
    errorSub: "Something went wrong on our side. Try again in a moment.",
    retry: "Try again",
  },
  ranking: {
    title: "Rankings · BeatFlipz",
    eyebrow: "Leaderboard",
    heading: "Rankings",
    sub: "The producers winning the most contests on the board. Every win is a contest closed and a prize paid.",
    producersRanked: "Producers ranked",
    contestsEntered: "Contests entered",
    contestsWon: "Contests won",
    sortBy: "Sort by",
    sorts: { won: "Contests Won", entered: "Contests Entered", winnings: "Winnings" },
    caption: "Producer rankings",
    name: "Name",
    entered: "Contests Entered",
    won: "Contests Won",
    winnings: "Winnings",
    avatar: (username: string) => `${username}, producer`,
    empty: "No producers ranked yet.",
    error: "Rankings didn't load. Try again in a moment.",
    swipe: "Swipe sideways to see every column.",
  },
  producerPage: {
    title: (username: string) => `@${username} · BeatFlipz`,
    loadingTitle: "Producer · BeatFlipz",
    eyebrow: "Producer",
    memberSince: (date: string) => `On BeatFlipz since ${date}`,
    avatar: (username: string) => `${username}, producer`,
    stats: "Record",
    rank: "Rank",
    entered: "Contests entered",
    won: "Contests won",
    winnings: "Winnings",
    links: { instagram: "Instagram", twitter: "X", soundcloud: "SoundCloud", website: "Website" },
    inNow: "In now",
    inNowSub: "Back them before voting opens.",
    odds: "Odds",
    pot: "Pot",
    fans: (n: number) => `${n} ${n === 1 ? "fan" : "fans"}`,
    back: "Back",
    viewContest: "View contest",
    voting: "Voting",
    wins: "Wins",
    place: (n: number) => (n === 1 ? "1st" : n === 2 ? "2nd" : n === 3 ? "3rd" : `${n}th`),
    notFoundEyebrow: "Not found",
    notFound: "No producer by that name",
    notFoundSub: "Check the spelling, or find them on the leaderboard.",
    rankings: "See the rankings",
    error: "This page didn't load",
    errorSub: "Something went wrong on our end. Try again in a moment.",
    retry: "Try again",
  },
  contest: {
    title: (name: string) => `${name} · BeatFlipz`,
    loadingTitle: "Contest · BeatFlipz",
    allContests: "All contests",
    edit: "Edit",
    notFoundEyebrow: "Not found",
    notFound: "This contest isn't here",
    notFoundSub: "It may have been taken down, or the link is wrong. Live contests are a click away.",
    browse: "Browse contests",
    error: "This contest didn't load",
    errorSub: "Something went wrong on our side. Try again in a moment.",
    retry: "Try again",
    cover: (name: string) => `${name} cover art`,
    seeded: "House seeded",
    seededNote: "Seeded by BeatFlipz. Pool volume is not all organic.",
    states: {
      draft: "Draft",
      open: "Open",
      closed: "Closed",
      judging: "Judging",
      complete: "Complete",
      cancelled: "Cancelled",
    },
    stateNote: {
      draft: "Not published yet. Only you can see this.",
      open: "Taking entries",
      closed: "Submissions closed",
      judging: "Entries are in. Judging is underway.",
      complete: "Winners are in.",
      cancelled: "This contest was cancelled.",
    },
    closesIn: "Submissions close in",
    pool: {
      label: "Pool",
      backers: (n: number) => (n === 1 ? "1 backer" : `${n} backers`),
      fee: (rake: string) => `${rake} platform fee`,
      bonus: (amount: string) => `+ ${amount} house bonus`,
      cta: "Make a Pick",
    },
    views: (n: number) => `${n.toLocaleString("en-US")} ${n === 1 ? "view" : "views"}`,
    hostedBy: "Hosted by",
    versus: "Versus",
    lateMessage: "Note from the host",
    cancelledReason: "Why it was cancelled",
    tablist: "Contest sections",
    tabs: { details: "Details", join: "Join", vote: "Vote", activity: "Activity", reviews: "Reviews" },
    details: {
      properties: "Properties",
      prize: "Prize",
      pool: "Pool",
      cashPrize: "Cash prize",
      noPrize: "None",
      admission: "Admission",
      free: "Free",
      entry: (display: string) => `${display} entry`,
      payAsYouGo: "Pay per entry",
      seats: "Seats",
      seatsValue: (taken: number, total: number) => `${taken} / ${total}`,
      seatsLeft: (n: number) => (n === 1 ? "1 seat open" : n === 0 ? "Full" : `${n} seats open`),
      genre: "Genre",
      challenge: "Challenge",
      duration: "Duration",
      ends: "Ends",
      extras: "Extras",
      samplePack: "Sample pack included",
      sponsor: "Sponsor",
      winners: "Winners",
      place: (n: number) => (n === 1 ? "1st" : n === 2 ? "2nd" : n === 3 ? "3rd" : `${n}th`),
      judge: "Judge the entries",
      judgeNote: "You can vote on this contest.",
    },
    activity: {
      heading: "Activity",
      kinds: { entry: "Entry", submission: "Submission", review: "Review", result: "Result" },
    },
    reviews: {
      heading: "Track reviews",
      average: "Average rating",
      count: (n: number) => `${n} ${n === 1 ? "review" : "reviews"}`,
      stars: (n: number) => `${n} out of 5 stars`,
      on: (track: string) => `on ${track}`,
      by: (producer: string) => `by @${producer}`,
      watch: "Watch the review",
      listen: "Hear the review",
    },
    ago: {
      now: "just now",
      minutes: (n: number) => `${n}m ago`,
      hours: (n: number) => `${n}h ago`,
      days: (n: number) => `${n}d ago`,
    },
    backMe: {
      heading: "Get your fans to back you",
      sub: "Share this link on your story. It opens this contest with you already in their bet slip.",
      share: "Share your Back Me link",
      copied: "Link copied",
      manual: "Copy this link:",
      shareTitle: (contest: string) => `Back me in ${contest}`,
      shareText: (username: string, contest: string) => `I'm in ${contest} on BeatFlipz. Back @${username} before picks close.`,
    },
    vote: {
      heading: "Back a producer",
      sub: "Put credits behind the producer you think takes it. The pool is split between the winning producer and the fans who called it.",
      pool: "Pool",
      backers: "Backers",
      producers: "Producers",
      balance: "Your balance",
      yourPicks: "Your picks",
      // The platform fee, stated plainly on the pool header (invariant 17).
      fee: (rake: string, producer: string) =>
        `${rake} platform fee and ${producer} to the winning producer come off the pool before it is split.`,
      limits: (min: string, cap: string) => `Picks from ${min} up to ${cap} per person.`,
      bonus: (amount: string) => `+ ${amount} house bonus for fans who back the winner, fee-free.`,
      house: (amount: string) => `${amount} of the pool is house backing.`,
      picksClosed: "Picks closed",
      lockedNote: "Picks are closed. Odds are frozen until the winner is picked.",
      entrant: "You're in this contest, so you can't back anyone in it.",
      unverified: "Verify your email to back a producer.",
      verify: "Resend verification email",
      placed: (producer: string) => `Pick placed on @${producer}.`,
      failed: "Your pick didn't go through. Nothing was taken.",
      insufficient: "Insufficient funds. Opening checkout to add credits…",
      insufficientNoCheckout: "Insufficient funds. Add credits from your dashboard, then try again.",
      voidedHeading: "Every stake was refunded",
      voidedGeneric: "This market was called off.",
      refund: (amount: string) => `Your ${amount} is back in your balance.`,
      refundPending: (amount: string) => `Your ${amount} refund is on its way.`,
      disclaimer:
        "Odds come from the pool and move until picks close. Nobody sets them, and nothing here is guaranteed.",
    },
    reveal: {
      heading: "The results",
      winner: "Winner",
      table: "Final pool shares",
      producer: "Producer",
      share: "Share",
      odds: "Final odds",
      payoutHeading: "Your payout",
      won: "You called it.",
      pending: "Paying out",
      paid: "Paid to your balance",
      lost: "Not this time.",
      lostSub: "Your producer didn't take it. There's another battle coming.",
      split: (pool: string, rake: string, producer: string) =>
        `Pool ${pool}. Platform fee ${rake}. Winning producer ${producer}.`,
    },
    join: {
      heading: "Join this contest",
      signIn: "Log in to join",
      signInSub: "You need an account to reserve a seat or submit a beat.",
      verifyHeading: "Verify your email",
      verifySub: "Check your inbox for the link we sent, then come back to join.",
      verify: "Resend verification email",
      closedHeading: "Submissions are closed",
      closedSub: "This contest isn't taking entries any more.",
      sponsorHeading: "You're hosting this one",
      sponsorSub: "Hosts can't enter their own contest.",
      fullHeading: "This contest is full",
      fullSub: "Every seat is taken. New battles open every week.",
      freeHeading: "Join for free",
      freeSub: "Reserve a seat, then upload your beat before the deadline.",
      joinFree: "Join free",
      paidHeading: "Pay for entry",
      paidSub: (fee: string) => `Entry is ${fee}. Credits in your balance go first; any rest is paid at checkout.`,
      payEntry: (fee: string) => `Pay ${fee} entry`,
      paypal: "Check out with PayPal",
      working: "Working…",
      submitHeading: "Submit your beat",
      submitSub: "Upload an mp3 or wav, up to 150 MB.",
      chooseFile: "Choose a file",
      submit: "Submit beat",
      uploading: (pct: number) => `Uploading ${pct}%`,
      badFile: "Choose an mp3 or wav file under 150 MB.",
      submitted: "Your beat is in. Good luck.",
      submittedHeading: "You're entered",
      submittedSub: "Your beat is in. Watch the Activity tab for results.",
      cosignHeading: "Get a co-signer",
      cosignSub: "No budget for entry? A co-signer can back your seat, and you share the upside if you win.",
      cosign: "Copy co-sign link",
      cosignCopied: "Co-sign link copied",
      cosignManual: "Copy this link:",
      failed: "Something went wrong. Try again.",
    },
  },
  dashboard: {
    title: "Dashboard · BeatFlipz",
    eyebrow: "Creator dashboard",
    welcome: "Welcome",
    desktopKey: "Desktop key",
    ranking: "Ranking",
    loading: "Loading your dashboard…",
    error: "Your dashboard didn't load.",
    retry: "Try again",
    verifyBanner: "Verify your email to enter contests and back picks.",
    resend: "Resend verification email",
    sections: "Dashboard sections",
    tabs: { profile: "Profile", photo: "Profile Photo", inbox: "Inbox", withdrawals: "Withdrawals" },
    profile: {
      username: "Username",
      usernameHelp: "This is how you appear on contests and the rankings board.",
      website: "Website",
      websitePlaceholder: "https://yoursite.com",
      bio: "Bio",
      bioPlaceholder: "Short bio",
      role: "Describe yourself",
      roles: [
        { value: "artist", label: "Artist" },
        { value: "producer", label: "Producer" },
        { value: "both", label: "Both" },
      ],
      notify: "Turn on beat challenges",
      notifyLabel: "Notify me for battles",
      budget: "1v1 budget",
      genres: "Preferred genres",
      location: "Location",
      city: "City",
      state: "State",
      country: "Country",
      links: "Links",
      instagram: "Instagram",
      soundcloud: "SoundCloud",
      twitter: "X / Twitter",
      cashApp: "Cash App",
      paypal: "PayPal email",
      paypalHelp: "Where winnings are sent unless you name another address when you withdraw.",
      save: "Save profile",
      saving: "Saving…",
      saved: "Profile saved",
      nothing: "Nothing to save yet.",
      memberSince: (date: string) => `Member since ${date}`,
    },
    photo: {
      heading: "Your producer photo",
      sub: "This shows on your contest cards and leaderboard row. Square images work best, at least 400×400px.",
      alt: (username: string) => `${username} profile photo`,
      none: "No photo",
      pick: "Click to upload a new photo",
      rules: "PNG, JPG, or WebP · up to 8MB",
      inputLabel: "Upload profile photo",
      badType: "That file isn't a PNG, JPG, or WebP.",
      tooBig: "That image is larger than 8MB. Try a smaller one.",
      save: "Save photo",
      saving: "Uploading…",
      remove: "Remove photo",
      saved: "Profile photo updated.",
      removed: "Profile photo removed.",
      failed: "The upload didn't go through. Try again.",
    },
    inbox: {
      all: "All",
      unread: "Unread",
      markAll: (n: number) => `Mark all read (${n})`,
      markRead: "Mark as read",
      markUnread: "Mark as unread",
      empty: "No messages yet.",
      emptyFilter: "Nothing here right now.",
      pick: "Pick a message to read it here.",
      kinds: { system: "BeatFlipz", contest: "Contest", payout: "Payout", fan: "Review" },
    },
    withdrawals: {
      available: "Available to withdraw",
      availableSub: "Winnings only. Bought credits can be spent, not withdrawn.",
      pending: "Pending",
      pendingSub: "Requested and waiting to be paid.",
      heading: "Request a withdrawal",
      sub: (min: string) => `Winnings of ${min} or more can be withdrawn to PayPal.`,
      amount: "Amount to withdraw",
      paypal: "PayPal email",
      submit: "Request withdrawal",
      submitting: "Requesting…",
      enterAmount: "Enter an amount in dollars and cents.",
      belowMin: (min: string) => `The minimum withdrawal is ${min}.`,
      overAvailable: (available: string) => `You can withdraw up to ${available}.`,
      badEmail: "Enter the PayPal email to pay.",
      requested: (amount: string) => `Withdrawal of ${amount} requested. We'll email you when it's paid.`,
      receive: (amount: string) => `You'll receive ${amount}. Beatflipz pays the PayPal fee.`,
      confirm: "Requests are paid manually to the PayPal address above, usually within a few business days.",
      history: "Recent withdrawals",
      empty: "No withdrawals yet.",
      columns: { amount: "Amount", destination: "PayPal", requested: "Requested", status: "Status" },
      states: { pending: "Pending", completed: "Paid" } as Record<string, string>,
    },
    sidebar: {
      claimHeading: "Claim your producer homepage",
      claimSub: "Build your own site on BeatFlipz: beats, links, and a mailing list in one place.",
      claimCta: "Claim your producer homepage",
      calculatorHeading: "What's your number?",
      calculatorSub: "Set what you want to make this month and see the battles, wins, and fans it takes.",
      calculatorCta: "Open the calculator",
      credits: "Contest credits",
      available: "available",
      cosign: (display: string) => `${display} in co-sign credits`,
      buy: (n: number) => `Buy ${n} credits`,
      noFees: "$25 for 25 credits, no added fees. Beatflipz pays the card fee.",
      redirecting: "Redirecting…",
      checkoutFailed: "Checkout didn't start. Try again in a minute.",
      won: "Contests won",
      entered: "Entered",
      myContests: "My contests",
      myRanking: "My ranking",
    },
  },
  admin: {
    title: "Control Room · BeatFlipz",
    eyebrow: "Super admin",
    heading: "Control Room",
    onlyHeading: "Admins only",
    onlyBody: "This page is for BeatFlipz admins. Sign in with an admin account to use it.",
    signIn: "Sign in",
    summary: (queued: number, users: number, payouts: number) =>
      `${queued} contests in queue · ${users} users · ${payouts} payouts pending`,
    tiles: { ready: "Ready", votes: "Votes", review: "Review" },
    sections: "Control Room sections",
    tabs: {
      ready: "Ready to Judge",
      votes: "Votes",
      review: "Review",
      users: "Users",
      won: "Latest Won Contests",
      withdrawals: "Withdrawals",
      platform: "Platform",
      usage: "Usage",
      goals: "Goals",
    },
    loading: "Loading…",
    loadFailed: "This section didn't load.",
    retry: "Try again",
    more: "Show more",
    dismiss: "Dismiss",
    confirm: "Confirm",
    cancel: "Cancel",
    working: "Working…",
    emptyQueue: "Nothing in this queue",
    emptyQueueSub: "No contests match the current filters.",
    filters: {
      heading: "Search contests",
      found: (n: number) => `${n} found`,
      producer: "Producer",
      producerPlaceholder: "Username",
      prize: "Cash prize ($)",
      contestants: "Seats",
      admission: "Admission ($)",
      min: "Min",
      max: "Max",
      genre: "Genre",
      challenge: "Challenge",
      duration: "Duration",
      any: "Any",
      freeAdmission: "Free admission",
      versus: "1v1 only",
      search: "Search",
      reset: "Reset",
    },
    row: {
      id: "ID",
      edit: "Edit",
      judgePage: "Judge page",
      published: "Published",
      unpublished: "Unpublished",
      seeded: "Admin created",
      seats: (taken: number, total: number) => `${taken} / ${total} seats`,
      submissions: (n: number) => `${n} submissions`,
      created: "Created",
      starts: "Starts",
      ends: "Ends",
      duration: "Duration",
      views: "Views",
      reservations: "Reservations",
      entry: "Entry",
      prize: "Prize",
      pot: "Pot",
      producer: "Producer",
      votes: "Votes",
      rating: "Rating",
      noTallies: "No tallies recorded yet.",
      voted: "Voted",
      didNotVote: "Did not vote",
      nobody: "Nobody",
      notifyVote: "Notify to vote",
      sent: (when: string) => `Sent ${when}`,
      notSent: "Not sent yet",
      updateTallies: "Update tallies",
      pickWinner: (title: string) => `Pick a winner for ${title}`,
      chooseWinner: "Choose a winner",
      selectWinner: "Select winner",
      placementsNote: "This contest awards placements. Pick them on the judge page.",
      winner: (name: string) => `Winner: ${name}`,
      publish: "Publish",
      unpublish: "Unpublish",
      delete: "Delete",
      readyNote: "Ready for judges",
      reviewNote: "Ended with entries and no winner: needs review",
      preview: (title: string) => `Preview of ${title}`,
    },
    newContestLink: "New contest",
    newContest: {
      title: "New contest · Control Room",
      eyebrow: "Control Room",
      heading: "New contest",
      back: "Back to Control Room",
      details: "Details",
      name: "Name",
      namePlaceholder: "Boom Bap Battle",
      description: "Description",
      descriptionPlaceholder: "The brief producers see: the sample, the rules, what wins.",
      genre: "Genre",
      challenge: "Challenge",
      duration: "How long",
      choose: "Choose…",
      rules: "Seats and voting",
      seats: "Seats",
      seatsHint: "How many producers can enter, 2 to 300.",
      voting: "Voting",
      votingOptions: { public: "Public vote", judges: "Judges" } as Record<string, string>,
      votingHint: {
        public: "Signed-in users vote on the entries.",
        judges: "The platform picks the winner.",
      } as Record<string, string>,
      publish: "Publish now",
      publishHint: "Off saves it unpublished. Publish it later from the Control Room.",
      media: "Cover and files",
      cover: "Cover image",
      coverRules: "JPG, PNG, GIF, or WebP, up to 10 MB",
      files: "Sample files",
      filesRules: "MP3, WAV, or ZIP, up to 150 MB each",
      pick: "Choose a file",
      pickMany: "Choose files",
      remove: (name: string) => `Remove ${name}`,
      badCover: "The cover must be a JPG, PNG, GIF, or WebP under 10 MB.",
      badFile: (name: string) => `${name} isn't an MP3, WAV, or ZIP under 150 MB.`,
      summary: "Summary",
      entry: "Entry",
      free: "Free",
      prize: "Prize",
      prizeValue: "The market pool",
      winners: "Winners",
      oneWinner: "1",
      starts: "Starts",
      startsNow: "When created",
      summaryNote:
        "Fans back producers in the prediction market, and the pool is the prize. There's no admission fee, setup fee, or checkout.",
      submit: "Create contest",
      creating: "Creating…",
      uploading: (name: string, pct: number) => `Uploading ${name} (${pct}%)`,
      uploadFailed: (name: string) => `${name} didn't upload.`,
      retry: "Retry",
      created: "Contest created",
      createdBody: (title: string, published: boolean) =>
        published ? `${title} is live.` : `${title} is saved and unpublished.`,
      view: "View contest",
      another: "Create another",
      setUpMarket: "Set up market",
    },
    editContest: {
      title: "Edit contest · Control Room",
      heading: "Edit contest",
      view: "View contest",
      published: "Published",
      unpublished: "Unpublished",
      save: "Save changes",
      saving: "Saving…",
      saved: "Contest saved.",
      uploaded: "Files uploaded.",
      upload: "Upload files",
      taken: (n: number) => `${n} taken`,
      notFound: "That contest doesn't exist.",
    },
    market: {
      heading: "Prediction market",
      none: "No market yet. Open one and every producer who enters can be backed. The pool is the prize.",
      states: { open: "Open", locked: "Picks closed", settled: "Settled", voided: "Voided" } as Record<string, string>,
      fee: "Platform fee (%)",
      feeHint: "10 to 20%, taken from the pool.",
      producerCut: "Producer cut (%)",
      producerCutHint: "Paid to the winning producer from the pool.",
      takeLocked: "Picks are in, so the fee and producer cut are fixed.",
      minStake: "Minimum pick ($)",
      cap: "Per-person cap ($)",
      seed: "House bonus ($)",
      seedHint: "Added fee-free to what fans who back the winner split. Not paid if the market voids. It can go up, never down.",
      open: "Open market",
      opening: "Opening…",
      save: "Save market",
      saving: "Saving…",
      opened: "Market open. Producers can be backed as soon as they enter.",
      saved: "Market saved.",
      badNumber: "Enter amounts as numbers, like 15 or 2.50.",
      // What the platform is left with per $1 once it has paid the card and
      // payout fees out of its own cut (EPIC-024).
      netKeeps: (cents: string) => `Est. platform keeps ${cents}¢ per $1 after fees (worst case)`,
      netLoses: (cents: string) => `Est. platform loses ${cents}¢ per $1 after fees (worst case)`,
      tiles: { pool: "Pool", bonus: "Bonus", house: "House backing", picks: "Picks" },
      columns: { producer: "Producer", staked: "Staked", house: "House", backers: "Backers", odds: "Odds" },
      noOutcomes: "Nobody has been backed yet. Every entrant appears here once they're backed.",
      back: "Back a producer",
      backHint: "House money in the pool. If they win, the house's share returns to the platform; if not, it goes to the winning fans.",
      producer: "Producer",
      choose: "Choose…",
      noEntrants: "Nobody has entered yet, so there's no one to back.",
      amount: "Amount ($)",
      backButton: "Back producer",
      backing: "Backing…",
      confirmBack: (user: string, amount: string) =>
        `Back ${user} with ${amount} of house money? It joins the pool and can't be taken back.`,
      backed: (user: string) => `House backing placed on ${user}.`,
      voided: (reason: string) => `This market was voided${reason ? `: ${reason}` : ""}. Every pick was refunded.`,
    },
    confirmWinner: (user: string, title: string) =>
      `Make ${user} the winner of "${title}"? The prize is paid to their balance now and can't be undone here.`,
    confirmDelete: (title: string) =>
      `Delete "${title}"? A prediction market on it is voided and every pick refunded first.`,
    confirmLogin: (user: string) => `Sign in as ${user}? You'll leave the Control Room until you return from the menu.`,
    confirmDeactivate: (user: string) => `Deactivate ${user}? They can't sign in until reactivated. Nothing is deleted.`,
    confirmPaid: (amount: string, to: string) => `Mark ${amount} to ${to} as paid? Only do this after sending the PayPal payment.`,
    confirmReject: (amount: string, user: string) => `Reject ${user}'s ${amount} withdrawal? The amount goes back to their balance.`,
    results: {
      selected: (user: string) => `${user} is the winner. Their prize was paid.`,
      already_selected: (user: string) => `${user} was already the winner. Nothing more was paid.`,
      notified: "Vote reminders sent.",
      tallies: "Tallies updated.",
      published: "Contest published.",
      unpublished: "Contest unpublished.",
      deleted: "Contest deleted.",
      deactivated: (user: string) => `${user} is deactivated.`,
      reactivated: (user: string) => `${user} is active again.`,
      completed: "Marked paid.",
      already_completed: "Already marked paid.",
      rejected: "Rejected and refunded.",
      already_rejected: "Already rejected.",
    } as Record<string, string | ((s: string) => string)>,
    won: {
      columns: { contest: "Contest", winner: "Winner", prize: "Prize", pot: "Pot", contestants: "Contestants", won: "Won" },
      empty: "No contests have a winner yet.",
    },
    users: {
      search: "Search users",
      searchPlaceholder: "Username or email",
      columns: { user: "User", contact: "Email & PayPal", balance: "Balance" },
      counts: (subs: number, res: number) => `${subs} submissions · ${res} reservations`,
      joined: (when: string) => `Joined ${when}`,
      unverified: "Email not verified",
      paypal: (email: string) => `PayPal: ${email}`,
      instagram: (handle: string) => `Instagram: ${handle}`,
      deactivated: "Deactivated",
      adminBadge: "Admin",
      login: "Log in as",
      deactivate: "Deactivate",
      reactivate: "Reactivate",
      empty: "No users match this search.",
    },
    withdrawals: {
      states: { pending: "Pending", completed: "Paid", rejected: "Rejected" } as Record<string, string>,
      filter: "Withdrawal state",
      columns: { user: "User", paypal: "PayPal", amount: "Amount", requested: "Requested", state: "State" },
      markPaid: "Mark paid",
      reject: "Reject",
      empty: "No withdrawals in this state.",
      // The fee is typed at the moment the payout is sent; blank records the
      // list price the server already worked out (EPIC-024).
      paypalFee: "PayPal fee ($)",
      feeHint: "What PayPal charged. Leave it blank to record the list-price fee.",
      feeInvalid: "Enter the PayPal fee in dollars, like 0.50.",
      feeRecorded: (amount: string) => `PayPal fee ${amount}`,
    },
    platform: {
      columns: {
        contest: "Contest",
        contestants: "Entrants",
        pot: "Pot",
        revenue: "Revenue",
        prize: "Cash prize",
        paid: "Winner paid",
        rake: "Rake",
        cut: "Producer cut",
        dust: "Dust",
      },
      seeded: "Seeded",
      totals: "Totals",
      none: "—",
      marketsUnavailable: "Market numbers couldn't be read, so rake, producer cut, and dust are missing.",
      empty: "No contest has entrants yet.",
      net: {
        title: "Net by month",
        columns: { month: "Month", rake: "Rake", card: "Card fees", paypal: "PayPal fees", net: "Net" },
        estimated: (n: number) => `${n} PayPal ${n === 1 ? "fee" : "fees"} estimated at list price`,
        unavailable: "Fee numbers couldn't be read, so net by month is missing.",
      },
    },
    goals: {
      tiles: { savers: "Goals saved", members: "Members", visitors: "Visitors" },
      producers: "Producers",
      fans: "Fans",
      median: "Median goal",
      total: "Total asked for",
      money: "Money",
      buzz: "Buzz",
      genres: "Genres",
      recent: "Latest goals",
      visitor: "Visitor",
      columns: { who: "Who", role: "Side", goal: "Monthly goal", genre: "Genre", focus: "Focus", when: "Saved" },
      anyGenre: "Any genre",
      empty: "No goals saved yet. They come from Save my goal on the homepage calculator.",
    },
    usage: {
      tiles: { last7: "Last 7 days", last30: "Last 30 days", last90: "Last 90 days", ytd: "Year to date", total: "All time" },
      signups: "Signups",
      monthly: "Signups by month",
      recent: "Recent registrations",
      funnel: "Website editor funnel",
      siteConfigs: (n: number) => `${n} sites configured`,
      columns: {
        user: "User",
        email: "Email",
        joined: "Joined",
        verified: "Verified",
        type: "Type",
        loads: "Loads",
        publishes: "Publishes",
        purchases: "Purchase attempts",
      },
      yes: "Yes",
      no: "No",
      empty: "No data yet.",
      queried: (when: string) => `As of ${when}`,
    },
  },
  footer: {
    skill:
      "BeatFlipz is a skill-based competition. Fans predict the outcome of judged beat contests, and results are decided by the producers and listeners who vote, not by chance and not by a bookmaker setting odds.",
    credits:
      "Credits back picks inside contests. They are not a cash deposit. The platform fee is shown on every contest. Play only where you are eligible to.",
    elsewhere: "Elsewhere",
    websites: "Websites for producers",
    howItWorks: "How it works",
    rights: "© 2026 BeatFlipz",
  },
} as const;
